import React, { useState, useEffect, PropsWithChildren } from 'react'
import { AxiosError } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

import api from '../services/api'
import {
  createSession,
  refreshSession,
  logout,
  Credentials,
  SessionResult,
  UserData
} from '../services/session'
import { SESSION_KEY_NAME, IS_AUTH_KEY_NAME } from '../utils/constants'
import { pages } from '../config/pages'
import { sessionRefreshTime } from '../config/env'

interface Auth {
  user?: UserData
  permissions: string[]
  isAuth: boolean
  signIn: (credentials: Credentials) => Promise<void>
  signOut: () => void
  updateUserData: (data: UserData) => void
}

interface AccessTokenPayload {
  permissions: string[]
  role: string
}

export const AuthContext = React.createContext<Auth>({} as Auth)

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<UserData>()
  const [permissions, setPermissions] = useState<string[]>([])
  const [isAuth, setIsAuth] = useState(
    () => localStorage.getItem(IS_AUTH_KEY_NAME) === 'true'
  )
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  function handleSession(session: SessionResult) {
    const { accessToken, user } = session

    localStorage.setItem(SESSION_KEY_NAME, accessToken)
    localStorage.setItem(IS_AUTH_KEY_NAME, 'true')
    api.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    const payload = jwtDecode(accessToken) as AccessTokenPayload
    setUser(user)
    setPermissions(payload.permissions)
    setIsAuth(true)
  }

  function clearSession() {
    setUser(undefined)
    setIsAuth(false)
    localStorage.removeItem(SESSION_KEY_NAME)
    localStorage.removeItem(IS_AUTH_KEY_NAME)
    navigate(pages.login.path)
  }

  function onRefreshFail(err: AxiosError) {
    if (err.response?.status === 401) {
      clearSession()
    }
  }

  useEffect(() => {
    const token = localStorage.getItem(SESSION_KEY_NAME)
    if (!token) {
      setLoading(false)
      return
    }

    api.defaults.headers.common.Authorization = `Bearer ${token}`

    refreshSession()
      .then(session => {
        handleSession(session)
      })
      .catch(err => {
        onRefreshFail(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (isAuth && location.pathname === pages.login.path) {
      navigate(pages.home.path)
    }
  }, [isAuth, location.pathname, navigate])

  useEffect(() => {
    const refreshIntervalId = setInterval(() => {
      if (localStorage.getItem(IS_AUTH_KEY_NAME) === 'true') {
        refreshSession()
          .then(session => handleSession(session))
          .catch(onRefreshFail)
      }
    }, sessionRefreshTime)
    return () => clearInterval(refreshIntervalId)
  }, [])

  function onSignInFail(err: AxiosError<any>) {
    if (err.response?.status !== 401) return

    toast.error(err.response.data.message)
  }

  async function signIn(credentials: Credentials) {
    const { email, password } = credentials

    return Promise.resolve()
      .then(() => createSession({ email, password }))
      .then(session => handleSession(session))
      .catch(err => onSignInFail(err))
  }

  function signOut() {
    Promise.resolve()
      .then(() => logout())
      .then(() => clearSession())
  }

  function updateUserData(data: UserData) {
    setUser(data)
  }

  if (loading) {
    return null
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,
        isAuth,
        signIn,
        signOut,
        updateUserData
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
